import { useQuery } from '@tanstack/react-query';
import type { SyncWatermarkEntry } from '@avisoma/shared';
import backend from '../services/backend';
import { Card } from '../components/Card';
import { Skeleton } from '../components/Skeleton';
import { formatDateTime } from '../lib/dateFormat';
import { ModelVersionTable } from './ModelVersionTable';
import { ModelSyncHealth } from './ModelSyncHealth';

// sync_watermarks.direction is 'outbound' (export-feedback) / 'inbound'
// (ingest-feedback) — the only two directions the feedback jobs write. publish
// and pull track state in model_versions, not watermarks.
const DIRECTION_LABEL: Record<string, string> = {
  outbound: 'Last feedback export',
  inbound: 'Last feedback ingest',
};

function WatermarkFreshness({ watermarks }: { watermarks: SyncWatermarkEntry[] }) {
  if (watermarks.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">No feedback export or ingest recorded yet.</p>;
  }

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {watermarks.map((watermark) => (
        <div key={watermark.id} className="text-sm">
          <span className="text-gray-500 dark:text-zinc-400">
            {DIRECTION_LABEL[watermark.direction] ?? `Last ${watermark.direction}`} ({watermark.peerEnv}):
          </span>{' '}
          <span className="text-gray-900 dark:text-zinc-100">{formatDateTime(watermark.updatedAt)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ModelRegistryCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['system', 'model-versions'],
    queryFn: () => backend.getModelVersions().then((res) => res.data),
  });

  return (
    <Card
      header={
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-zinc-100">Model Registry</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            Local model versions, cross-environment sync health, and feedback-sync freshness.
          </p>
        </div>
      }
    >
      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}

      {!isLoading && error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error instanceof Error ? error.message : 'Failed to load model registry'}
        </p>
      )}

      {!isLoading && !error && data && (
        <>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Sync health</h3>
            <ModelSyncHealth heartbeats={data.heartbeats ?? []} />
          </div>
          <WatermarkFreshness watermarks={data.watermarks} />
          {data.versions.length > 0 ? (
            <ModelVersionTable versions={data.versions} />
          ) : (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              No model versions recorded yet. Versions appear here after a model is published or pulled.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
